// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppIcon, IconButton, TagChipGroup, TagPicker } from '@loa/ui-components';
import { fundsApi } from '../../api/fundsApi';
import { useTags } from '../../contexts/TagsContext';
import type { TagDTO } from '../../types/tag';
import './EntityTagEditor.css';

export interface FundTagEditorProps {
  fundKey: string;
  /** Currently-assigned tag ids for this fund (from FundDTO.tagIds / batch decoration). */
  tagIds: string[];
  /** Called with the fresh tagId list after a successful edit. */
  onTagIdsChange?: (tagIds: string[]) => void;
  maxChips?: number;
  className?: string;
}

/**
 * Fund federation tag editor (Tags PRD §6.4/FR-19). Funds carry only
 * `tagIds` — names/colours are resolved against the cached central tag
 * list (`useTags()`); an id that doesn't resolve (deleted mid-propagation)
 * renders nothing rather than throwing. Assignment writes go to
 * funds-service (`fundsApi.replaceFundTags`), never `entity_tags`.
 */
export function FundTagEditor({
  fundKey,
  tagIds,
  onTagIdsChange,
  maxChips = 3,
  className = '',
}: Readonly<FundTagEditorProps>) {
  const { tags: allTags, loading: tagsLoading, featureEnabled, createTag } = useTags();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>(tagIds);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => setSelectedIds(tagIds), [tagIds]);

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition({ top: rect.bottom + 4, left: rect.left });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (!featureEnabled) return null;

  const resolvedTags: TagDTO[] = selectedIds
    .map((id) => allTags.find((t) => t.id === id))
    .filter((t): t is TagDTO => !!t);

  const pickerTags = allTags.filter((t) => !t.archived || selectedIds.includes(t.id));

  const replace = async (nextIds: string[]) => {
    const previous = selectedIds;
    setSelectedIds(nextIds);
    setSaving(true);
    setError(null);
    try {
      const result = await fundsApi.replaceFundTags(fundKey, nextIds);
      setSelectedIds(result);
      onTagIdsChange?.(result);
    } catch (err) {
      setSelectedIds(previous);
      setError(err instanceof Error ? err.message : 'Failed to update tags');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (tagId: string) => {
    const next = selectedIds.includes(tagId)
      ? selectedIds.filter((id) => id !== tagId)
      : [...selectedIds, tagId];
    void replace(next);
  };

  const handleCreate = async (name: string) => {
    setSaving(true);
    setError(null);
    try {
      const created = await createTag({ name });
      await replace([...selectedIds, created.id]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tag');
    } finally {
      setSaving(false);
    }
  };

  const popover = open && position ? createPortal(
    <div ref={popoverRef} className="entity-tag-editor__popover" style={{ top: position.top, left: position.left }}>
      <TagPicker
        tags={pickerTags.map((t) => ({ id: t.id, name: t.name, color: t.color, archived: t.archived ?? false }))}
        selectedIds={selectedIds}
        onToggle={handleToggle}
        onCreate={handleCreate}
        loading={tagsLoading}
        error={error}
        creating={saving}
      />
    </div>,
    document.body,
  ) : null;

  return (
    <div className={`entity-tag-editor ${className}`.trim()}>
      <TagChipGroup
        tags={resolvedTags.map((t) => ({ id: t.id, name: t.name, color: t.color, archived: t.archived ?? false }))}
        max={maxChips}
      />
      <span ref={triggerRef} className="entity-tag-editor__trigger-anchor">
        <IconButton
          variant="ghost"
          size="xs"
          aria-label="Edit tags"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          icon={<AppIcon name="edit" size={14} />}
        />
      </span>
      {popover}
    </div>
  );
}
